import { useJsApiLoader } from '@react-google-maps/api';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateBookingData,
  setQueryParams,
  getUserProfile,
  getTimeZone,
  setProvince,
  clearPaymentLoad,
  getNearestCityByCoordinates,
} from 'sharedlibs/lib/redux/actions';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import MainWrapper from '../../components/MainWrapper';
import SquareButton from '../../components/SquareButton';
import Map from '../../components/Map';

import { Button, FooterRow, Wrapper } from './styles';
import { PROVINCES_OF_WORK } from 'sharedlibs/lib/utils/provinces';
import convertMapsToAddress from 'sharedlibs/lib/utils/convertMapsToAddress';
import getAddressDetail from 'sharedlibs/lib/utils/getAddressDetail';
import {
  buildAddressString,
  getBooking,
} from 'sharedlibs/lib/redux/actions/tempBooking';
import { useAuth0 } from '@auth0/auth0-react';
import { Col } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import convertHexToString from 'sharedlibs/lib/utils/convertHexToStr';
import api from 'sharedlibs/lib/api';
import LeadsModal from '../../components/LeadsModal';

const LocationsPage = ({
  onStepSelect,
  viewStepperMessage,
  setViewStepperMessage,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'mm-map-script',
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAPS_API}`,
    libraries: ['places'],
  });
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { hexData = '' } = useParams();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const user = useSelector(({ userReducer }) => userReducer);
  const queryParams = useSelector(({ queryReducer }) => queryReducer);
  const locationInfo = useSelector(({ bookingReducer, tempBookingReducer }) =>
    !hexData ? bookingReducer : tempBookingReducer
  );
  const [mapLoading, setMapLoading] = useState(false);
  const [address, setAddress] = useState({
    pickup: null,
    dropoff: null,
    pickupInput: '',
    dropoffInput: '',
    pickupDetail: '',
    dropoffDetail: '',
  });
  const [invalidMessage, setInvalidMessage] = useState('');
  const [invalidProvince, setInvalidProvince] = useState(false);
  const [bookingId, setBookingId] = useState();

  /**
 /* Google Tag Metric
  */
  useEffect(() => {
    window.gtag('event', 'page_view', {
      page_title: 'Locations',
      page_location: '/locations',
      page_path: '/locations',
      send_to: process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
    });

    window.gtag('event', 'conversion', {
      'send_to': `AW-${process.env.REACT_APP_GOOGLE_ADS_ID}/pBbfCK7HxNYBEJmF0awC`,
    });
  }, []);

  useEffect(() => {
    const stringData = convertHexToString(hexData);
    const [email, shortId] = stringData.split(' ');

    if (email && shortId) {
      try {
        dispatch(getBooking(encodeURIComponent(email), shortId));
      } catch (e) {
        console.error(e);
      }
    }
  }, [hexData]);

  useEffect(() => {
    setBookingId(locationInfo?.id || undefined);
  }, [locationInfo]);

  useEffect(() => {
    dispatch(clearPaymentLoad());
  }, []);
  /**
   /* Convert data from Redux Store
   */
  useEffect(() => {
    async function handleAddressDetail() {
      setMapLoading(true);
      const pickupAddress =
        locationInfo.pickup.fullAddress ||
        buildAddressString(locationInfo.pickup);
      const dropoffAddress =
        locationInfo.dropoff.fullAddress ||
        buildAddressString(locationInfo.dropoff);

      const pickupDetail = await getAddressDetail(pickupAddress);
      const dropoffDetail = await getAddressDetail(dropoffAddress);
      setAddress({
        pickupDetail,
        dropoffDetail,
        pickup: {
          pickup: pickupAddress,
          location: pickupDetail?.geometry?.location,
          aptUnit: locationInfo?.pickupAptUnit || '',
          hasElevator: locationInfo?.pickupHasElevator || null,
          floor: locationInfo?.pickupFloor || null,
          hasParking: `${locationInfo?.pickupHasParking}` || '1', // DEFAULT "no"
        },
        dropoff: {
          dropoff: dropoffAddress,
          location: dropoffDetail?.geometry?.location,
          aptUnit: locationInfo?.dropoffAptUnit || '',
          hasElevator: locationInfo?.dropoffHasElevator || null,
          floor: locationInfo?.dropoffFloor || null,
          hasParking: `${locationInfo?.dropoffHasParking}` || '1', // DEFAULT "no"
        },
        pickupInput: pickupAddress,
        dropoffInput: dropoffAddress,
      });
      setMapLoading(false);
    }
    if (
      !queryParams?.pickupFullAddress &&
      ((locationInfo?.pickup?.postalCode &&
        locationInfo?.dropoff?.postalCode) ||
        (locationInfo?.pickup?.city && locationInfo?.dropoff?.city) ||
        (locationInfo?.pickup?.fullAddress &&
          locationInfo?.dropoff?.fullAddress))
    ) {
      handleAddressDetail();
    }
  }, [locationInfo]);

  // Try to get the User Data on App Launch Page
  useEffect(() => {
    const getProfile = async () => {
      try {
        const accessToken = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'read:current_user',
        });
        await dispatch(getUserProfile(accessToken));
      } catch (e) {
        console.error(e);
      }
    };
    if (isAuthenticated && !user.email) {
      getProfile();
    }
  }, []);
  /**
  /* Convert url params route
  */
  useEffect(() => {
    function convertParking(parking) {
      return (
        {
          'false': 1,
          'true': 2,
        }[`${parking}`.toLowerCase()] || 1
      );
    }
    function convertAccessibility(accessibility) {
      return (
        {
          'stairs': 1,
          'elevator': 2,
        }[`${accessibility}`.toLowerCase()] || 0
      );
    }

    async function handleAddressDetail() {
      setMapLoading(true);
      const pickupDetail = await getAddressDetail(
        queryParams.pickupFullAddress
      );
      const dropoffDetail = await getAddressDetail(
        queryParams.dropoffFullAddress
      );

      const checkAddress = (addressDetail) => {
        const placeTypes = addressDetail.address_components
          ?.map(({ types }) => types)
          .join();
        return ['street_number', 'route'].every((type) =>
          placeTypes.includes(type)
        );
      };
      setAddress({
        pickupDetail,
        pickupFailed: !checkAddress(pickupDetail),
        dropoffDetail,
        dropoffFailed: !checkAddress(dropoffDetail),
        pickup: {
          pickup: pickupDetail?.formatted_address,
          location: pickupDetail?.geometry?.location,
          aptUnit: queryParams?.pickupApt || '',
          floor: queryParams?.pickupFloor || null,
          hasElevator:
            convertAccessibility(queryParams?.pickupAccessibility) || null,
          hasParking: convertParking(queryParams?.pickupParking),
        },
        dropoff: {
          dropoff: dropoffDetail?.formatted_address,
          location: dropoffDetail?.geometry?.location,
          aptUnit: queryParams?.dropoffApt || '',
          floor: queryParams?.dropoffFloor || null,
          hasElevator:
            convertAccessibility(queryParams?.dropoffAccessibility) || null,
          hasParking: convertParking(queryParams?.dropoffParking),
        },
        pickupInput: pickupDetail?.formatted_address,
        dropoffInput: dropoffDetail?.formatted_address,
      });
      dispatch(
        updateBookingData({
          drivingDuration: null,
          pickupHasElevator:
            queryParams.pickupAccessibility === 'stairs' ? 1 : 2,
          pickupAptUnit: queryParams?.pickupApt || '',
          pickupFloor: queryParams.pickupFloor
            ? parseInt(queryParams.pickupFloor)
            : 0,
          pickupHasParking: queryParams.pickupParking === 'true' ? 2 : 1,
          dropoffHasElevator:
            queryParams.dropoffAccessibility === 'stairs' ? 1 : 2,
          dropoffAptUnit: queryParams?.dropoffApt || '',
          dropoffFloor: queryParams.dropoffFloor
            ? parseInt(queryParams.dropoffFloor)
            : 0,
          dropoffHasParking: queryParams.dropoffParking === 'true' ? 2 : 1,
        })
      );
      setMapLoading(false);
    }
    if (queryParams?.pickupFullAddress) {
      handleAddressDetail();
    }
    return () => dispatch(setQueryParams({}));
  }, [queryParams]);

  useEffect(() => {
    if (invalidMessage && (!address.pickupInput || !address.dropoffInput)) {
      setInvalidMessage('');
    }
  }, [invalidMessage, address]);

  async function saveBooking() {
    const { pickup, dropoff, pickupDetail, dropoffDetail } = address;
    const pickupLocation = convertMapsToAddress(pickupDetail);
    const dropoffLocation = convertMapsToAddress(dropoffDetail);
    const { data } = await api.post('/location/limit', {
      origin: pickup.location,
      destination: dropoff.location,
    });
    if (data.out_of_limit) {
      return setInvalidMessage('error.outOfLimit');
    }
    dispatch(getTimeZone(pickup.location.lat, pickup.location.lng));
    dispatch(
      getNearestCityByCoordinates(
        'pickup',
        pickup.location.lat,
        pickup.location.lng
      )
    );
    dispatch(
      getNearestCityByCoordinates(
        'dropoff',
        dropoff.location.lat,
        dropoff.location.lng
      )
    );
    dispatch(setProvince(pickupLocation.province));
    dispatch(
      updateBookingData(
        {
          drivingDuration: null,
          pickup: pickupLocation,
          pickupAddress: pickupLocation,
          pickupCoords: pickup.location,
          dropoff: dropoffLocation,
          dropoffAddress: dropoffLocation,
          dropoffCoords: dropoff.location,
          pickupAptUnit: pickup.aptUnit || '',
          dropoffAptUnit: dropoff.aptUnit || '',
        },
        !!bookingId
      )
    );
    history.push(`/move-type/${bookingId || ''}`);
  }

  function handleNext() {
    const hasProvinceInvalid = [];
    function serveredAreas(addressDetail) {
      if (addressDetail?.short_name) {
        return !!PROVINCES_OF_WORK.includes(addressDetail.short_name);
      }
    }
    if (address?.pickupDetail?.address_components) {
      const pickupWorks =
        address.pickupDetail.address_components.filter(serveredAreas);
      if (!pickupWorks.length) hasProvinceInvalid.push('pickup');
    }
    if (address?.dropoffDetail?.address_components) {
      const dropoffWorks =
        address.dropoffDetail.address_components.filter(serveredAreas);
      if (!dropoffWorks.length) hasProvinceInvalid.push('dropoff');
    }
    if (!mapLoading) {
      if (hasProvinceInvalid.length) {
        setInvalidProvince(!!hasProvinceInvalid.length);
        setInvalidMessage('error.invalidArea');
      } else {
        setInvalidProvince(false);
        saveBooking();
      }
    }
  }

  const renderBackButton = !bookingId ? null : (
    <SquareButton
      onClick={() => {
        history.goBack();
      }}
      css="margin-top: 1em;"
      variant="tertiary"
    >
      <FormattedMessage id="action.back" />
    </SquareButton>
  );
  const canNext = !(
    address.pickup &&
    address.dropoff &&
    !mapLoading &&
    !address.pickupFailed &&
    !address.dropoffFailed &&
    !invalidProvince &&
    !invalidMessage
  );
  return (
    <MainWrapper
      logoPath={!bookingId ? '/' : '/profile/bookings'}
      stepper
      nextButton={null}
      backButton={null}
      editMode={!!bookingId}
      onStepSelect={onStepSelect}
      viewStepperMessage={viewStepperMessage}
      setViewStepperMessage={setViewStepperMessage}
      bookingIdVal
    >
      <Wrapper>
        {isLoaded && !loadError && (
          <>
            <Map
              editMode={!!bookingId}
              address={address}
              setAddress={setAddress}
              invalidMessage={
                !invalidMessage
                  ? ''
                  : intl.formatMessage({ id: invalidMessage })
              }
              canNext={canNext}
              handleNext={handleNext}
            />
            <FooterRow justify={'space-between'}>
              {renderBackButton && <Col span={11}>{renderBackButton}</Col>}
              <Col span={renderBackButton ? 11 : 24}>
                <Button
                  disabled={canNext}
                  onClick={handleNext}
                >
                  <FormattedMessage id="action.next" />
                  <RightOutlined />
                </Button>
              </Col>
            </FooterRow>
          </>
        )}
        {loadError && <h2>Maps not loading</h2>}
      </Wrapper>
      <LeadsModal
        open={
          process.env.NODE_ENV !== 'development' &&
          !queryParams.skipLeadForm &&
          !bookingId &&
          !user?.email &&
          !isAuthenticated &&
          !locationInfo.pickup.fullAddress &&
          !locationInfo?.lead?.submitted &&
          !hexData
        }
      />
    </MainWrapper>
  );
};

export default LocationsPage;
